
import { format, formatDistance, isValid } from 'date-fns';
import { devError } from '@/utils/devLog';

/**
 * Safely parse a date string and return a Date object or null if invalid
 */
export const safeDate = (dateString?: string | null): Date | null => {
  if (!dateString) return null;
  
  try {
    // ADDED: More detailed logging for date parsing
    const date = new Date(dateString);
    const isValidDate = isValid(date);
    
    if (!isValidDate) {
      console.warn(`⚠️ Invalid date string detected: "${dateString}"`);
    }
    
    return isValidDate ? date : null;
  } catch (error) {
    devError("Invalid date encountered:", dateString);
    return null;
  }
};

/**
 * Safely format a date using date-fns format
 * @param date Date to format
 * @param formatStr Format string for date-fns
 * @param fallback Fallback string to return if date is invalid
 */
export const formatDate = (date: Date | string | null | undefined, formatStr: string = 'MMM d, yyyy', fallback: string = 'Invalid date'): string => {
  try {
    if (!date) return fallback;
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (!dateObj || !isValid(dateObj)) {
      return fallback;
    }
    
    return format(dateObj, formatStr);
  } catch (error) {
    devError('Error formatting date:', error);
    return fallback;
  }
};

/**
 * Safely format a date with a default format
 * This is an alias for formatDate with default arguments
 */
export const formatSafeDate = (date: Date | string | null | undefined, formatStr: string = 'MMM d, yyyy'): string => {
  return formatDate(date, formatStr);
};

/**
 * Safely compare dates, accounting for null/invalid values
 * Returns true if date1 is after date2
 */
export const isDateAfter = (date1: Date | string | null, date2: Date | string | null): boolean => {
  const d1 = safeDate(typeof date1 === 'string' ? date1 : date1?.toString());
  const d2 = safeDate(typeof date2 === 'string' ? date2 : date2?.toString());
  
  if (!d1 || !d2) return false;
  return d1 > d2;
};

/**
 * Get a date N days ago from now
 */
export const getDaysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

/**
 * Convert a date to a human-readable time distance from now
 */
export const distanceToNow = (date: string | Date): string => {
  try {
    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    
    if (!isValid(parsedDate)) {
      console.warn(`⚠️ Invalid date in distanceToNow: "${date}"`);
      return 'Invalid date';
    }
    
    return formatDistance(parsedDate, new Date(), { addSuffix: true });
  } catch (error) {
    devError('Error converting date to distance:', error);
    return 'Unknown time';
  }
};
